import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { createSystem, updateSystem } from "../../api/customer/system";
import produce from "immer";
import { normalizeError, hasMeaningfulErrors } from "../../operationFeedback";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  system: {},
  data: [],
  count: 0,
};

export const systemsController = {
  filters: [],
  page: 0,
  query: "",
  sort: "desc",
  sortBy: "created_at",
  view: "all",
};

function rejectNormalized(rejectWithValue, err, operationKey) {
  return rejectWithValue(normalizeError(err, { operationKey }));
}

const GENERIC_SAFE =
  /^(En uventet feil oppstod\.|Handlingen kunne ikke fullføres\.)$/;

function rejectedErrors(action, fallbackNb) {
  const msg = action.payload?.safeMessage;
  if (typeof msg === "string" && msg.trim() && !GENERIC_SAFE.test(msg)) {
    return [msg];
  }
  return [fallbackNb];
}

export const updateSystemAsync = createAsyncThunk(
  "systems/updatesystemAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateSystem(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "update_system");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "update_system");
    }
  }
);

export const createSystemAsync = createAsyncThunk(
  "systems/createSystemAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createSystem(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "create_system");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "create_system");
    }
  }
);

const systemSlice = createSlice({
  name: "systems",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(updateSystemAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(updateSystemAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.system = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(updateSystemAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke oppdatere anlegget.");
      })
      .addCase(createSystemAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(createSystemAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.system = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(createSystemAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke opprette anlegget.");
      });
  },
});

export const { reducer } = systemSlice;

export default systemSlice;
